<template>
  <div class="p-3">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="mb-0">CF Enterprise Bulk Automation</h5>
      <button class="btn btn-primary" :disabled="batchCreating" @click="initiateBulkEnterprise">
        <i class="fa fa-bolt mr-1"></i>
        <span v-if="batchCreating"><i class="fa fa-spinner fa-spin mr-1"></i> Creating Batch...</span>
        <span v-else>Initiate CF Enterprise in Bulk</span>
      </button>
    </div>
    <p class="text-muted mb-3">Select how many pending CF Enterprise tasks to queue into a new automation batch.</p>
    <div v-if="hasActiveBatch" class="alert mb-4" style="background:#fff8e1;border:1px solid #ffe082;border-left:4px solid #f9a825;border-radius:6px;padding:12px 16px;display:flex;align-items:center;gap:10px;">
      <i class="fa fa-exclamation-triangle" style="color:#f9a825;font-size:18px;flex-shrink:0;"></i>
      <div>
        <strong style="color:#7a5c00;">Batch In Progress</strong>
        <div style="font-size:13px;color:#8a6d00;margin-top:2px;">A batch is currently active. You cannot create a new batch until the existing one is complete or failed.</div>
      </div>
    </div>

    <div v-if="loadingBatches" class="text-center py-4">
      <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
    </div>
    <div v-else-if="batches.length === 0" class="alert alert-light text-muted text-center">
      No batches created yet.
    </div>
    <div v-else class="table-responsive">
      <table class="table table-bordered table-hover">
        <thead class="thead-light">
          <tr>
            <th>Batch ID</th>
            <th>Progress</th>
            <th>Total</th>
            <th>Created At</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="batch in batches" :key="batch.id">
            <td style="font-family:monospace;font-size:12px;">{{ batch.batchId }}</td>
            <td>
              <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;">
                <span v-if="domainCounts(batch).success" style="background:#e8f5e9;color:#2e7d32;border:1px solid #a5d6a7;border-radius:99px;padding:2px 9px;font-size:11px;font-weight:600;">
                  <i class="fa fa-check mr-1"></i>{{ domainCounts(batch).success }} Success
                </span>
                <span v-if="domainCounts(batch).failed" style="background:#fff3e0;color:#e65100;border:1px solid #ffcc80;border-radius:99px;padding:2px 9px;font-size:11px;font-weight:600;">
                  <i class="fa fa-times mr-1"></i>{{ domainCounts(batch).failed }} Failed
                </span>
                <span v-if="domainCounts(batch).inProgress" style="background:#e3f2fd;color:#1565c0;border:1px solid #90caf9;border-radius:99px;padding:2px 9px;font-size:11px;font-weight:600;">
                  <i class="fa fa-spinner mr-1"></i>{{ domainCounts(batch).inProgress }} In Progress
                </span>
                <span v-if="domainCounts(batch).pending" style="background:#f5f5f5;color:#757575;border:1px solid #e0e0e0;border-radius:99px;padding:2px 9px;font-size:11px;font-weight:600;">
                  <i class="fa fa-clock-o mr-1"></i>{{ domainCounts(batch).pending }} Pending
                </span>
                <span v-if="domainCounts(batch).followUp" style="background:#fff8e1;color:#f57f17;border:1px solid #ffe082;border-radius:99px;padding:2px 9px;font-size:11px;font-weight:600;">
                  <i class="fa fa-flag mr-1"></i>{{ domainCounts(batch).followUp }} Follow-up
                </span>
              </div>
            </td>
            <td>{{ batch.totalCount }}</td>
            <td>{{ formatDate(batch.createdAt) }}</td>
            <td class="text-center">
              <button class="btn btn-sm btn-outline-secondary" @click="$router.push({ name: 'cfEnterpriseBatchDetail', params: { batchId: batch.batchId } })">
                <i class="fa fa-list mr-1"></i> View Domains
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {db} from '@/firebase/init';
import swal from 'sweetalert2';

export default {
  name: 'CFProvisioning',
  data() {
    return {
      batchCreating: false,
      loadingBatches: false,
      batches: [],
      unsubscribe: null
    };
  },
  mounted() {
    this.listenBatches();
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  computed: {
    hasActiveBatch() {
      const done = ['complete', 'completed', 'failed', 'fail'];
      return this.batches.some(b => !done.includes(b.status));
    }
  },
  methods: {
    listenBatches() {
      this.loadingBatches = true;
      this.unsubscribe = db.collection('cfEnterpriseBatches')
        .orderBy('createdAt', 'desc')
        .onSnapshot(snapshot => {
          this.batches = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          this.loadingBatches = false;
        }, err => {
          console.error('listenBatches error', err);
          this.loadingBatches = false;
        });
    },
    statusBadge(status) {
      return {
        'badge-warning': status === 'pending',
        'badge-info': status === 'running' || status === 'in-progress' || status === 'inProgress',
        'badge-success': status === 'complete' || status === 'completed',
        'badge-danger': status === 'failed' || status === 'fail'
      };
    },
    statusLabel(status) {
      const map = {
        'pending': 'Pending',
        'running': 'Running',
        'in-progress': 'In Progress',
        'inProgress': 'In Progress',
        'complete': 'Complete',
        'completed': 'Complete',
        'failed': 'Failed',
        'fail': 'Failed'
      };
      return map[status] || status;
    },
    domainCounts(batch) {
      const counts = { success: 0, failed: 0, inProgress: 0, pending: 0, followUp: 0 };
      if (!batch.domains) return counts;
      batch.domains.forEach(function(d) {
        const s = d.status || '';
        if (s === 'follow-up') { counts.followUp++; return; }
        if (s === 'pass' || s === 'complete' || s === 'completed' || s === 'success') counts.success++;
        else if (s === 'fail' || s === 'failed' || s === 'error') counts.failed++;
        else if (s === 'running' || s === 'in-progress' || s === 'inProgress') counts.inProgress++;
        else counts.pending++;
      });
      return counts;
    },
    formatDate(val) {
      if (!val) return '—';
      const d = val.toDate ? val.toDate() : new Date(val);
      return d.toLocaleString();
    },
    async initiateBulkEnterprise() {
      const result = await swal({
        title: 'Bulk CF Enterprise Automation',
        html: `
          <p class="mb-2">How many pending tasks do you want to automate?</p>
          <input id="swal-batch-count" type="number" min="1" max="100" value="5"
            class="swal2-input" style="width:120px;text-align:center;" />
        `,
        showCancelButton: true,
        confirmButtonText: 'Create Batch',
        preConfirm: () => {
          const val = parseInt(document.getElementById('swal-batch-count').value, 10);
          if (!val || val < 1 || val > 100) {
            swal.showValidationMessage('Please enter a number between 1 and 100');
            return false;
          }
          return val;
        }
      });

      const count = result.value;
      if (!count) return;

      try {
        this.batchCreating = true;

        const domains = [];
        let lastDoc = null;

        while (domains.length < count) {
          let query = db.collection('domains')
            .where('cfEnterprise.status', '==', 'pending')
            .limit(count);
          if (lastDoc) query = query.startAfter(lastDoc);

          const snapshot = await query.get();
          if (snapshot.empty) break;

          for (let i = 0; i < snapshot.docs.length; i++) {
            if (domains.length >= count) break;
            const doc = snapshot.docs[i];
            const data = doc.data();
            const cfEnt = data.cfEnterprise || {};
            if (cfEnt.checkinBy) continue;
            if (cfEnt.followUp === true) continue;
            domains.push({
              ip: data.ip || '',
              id: doc.id,
              uid: data.uid || '',
              domain: data.domain || '',
              status: 'pending'
            });
          }

          lastDoc = snapshot.docs[snapshot.docs.length - 1];
          if (snapshot.docs.length < count) break;
        }

        if (!domains.length) {
          return swal('No Pending Tasks', 'There are no pending CF Enterprise tasks to queue.', 'info');
        }
        console.log(domains)
        // return
        const batchRef = db.collection('cfEnterpriseBatches').doc();
        await batchRef.set({
          batchId: batchRef.id,
          status: 'pending',
          createdAt: new Date(),
          totalCount: domains.length,
          domains
        });

        await this.$http.post('zone/batch-automate/run', {
          grant_type: 'token',
          batchId: batchRef.id
        });
      } catch (e) {
        console.error('initiateBulkEnterprise error', e);
        swal('Error', 'Something went wrong while creating the batch.', 'error');
      } finally {
        this.batchCreating = false;
      }
    }
  }
};
</script>
